import React, { useState, useEffect, useRef } from 'react';
import { authenticatedFetch, getUserProfile } from '../utils/auth';
import { getUserMapPreference, getMapOptions } from '../utils/mapUtils';
import { detectInputType, parseLatLng } from '../utils/locationUtils';
import { useLocationDetection } from '../hooks/useLocationDetection';
import { usePdfGeneration } from '../hooks/usePdfGeneration';
import { handleApiError } from '../utils/errorHandler';
import PdfButton from './common/PdfButton';
import { generateUnifiedPDF, generateSearchEvidencePDF } from '../utils/pdfGenerator';
import InteractiveMap from './common/InteractiveMap';
import { handleMapClick } from '../utils/mapClickHandler';
import LocationSourceMessage from './LocationSourceMessage';
import { useAddresses } from '../hooks/useAddresses';
import { Link } from 'react-router-dom';
import Modal from './common/Modal';
import DedupControl from './DedupControl';
import DedupTransactionModal from './DedupTransactionModal';

// ResultDisplay component for showing distance results
function ResultDisplay({ from, to, roadDistance, roadDuration, aerialDistance, loading, fromLabel = 'Address 1', toLabel = 'Address 2', isLocationInIndiaParam = true, onGeneratePDF = null }) {

  
  const [copied, setCopied] = React.useState({});
  const [hovered, setHovered] = React.useState({});
  const [pdfLoading, setPdfLoading] = React.useState(false);
  
  const handleCopy = (key, value, e) => {
    if (!value || value === 'N/A') return;
    navigator.clipboard.writeText(value);
    setCopied(prev => ({ ...prev, [key]: true }));
    const button = e?.target;
    if (button) {
      const originalText = button.textContent;
      const originalBg = button.style.backgroundColor;
      button.textContent = 'Copied!';
      button.style.backgroundColor = '#28a745';
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = originalBg;
        setCopied(prev => ({ ...prev, [key]: false }));
      }, 1500);
    } else {
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 1500);
    }
  };

  const handleGeneratePDF = async () => {
    console.log('handleGeneratePDF called, onGeneratePDF:', !!onGeneratePDF);
    if (!onGeneratePDF) return;
    
    setPdfLoading(true);
    try {
      console.log('Calling onGeneratePDF function...');
      await onGeneratePDF();
      console.log('onGeneratePDF completed successfully');
    } catch (error) {
      console.log('Error in handleGeneratePDF:', error);
      // Error handling without console.log
    } finally {
      setPdfLoading(false);
    }
  };

  const renderCopy = (key, value) => (
    <button
      title="Copy"
      onClick={e => handleCopy(key, value, e)}
      disabled={!value || value === 'N/A'}
      style={{
        backgroundColor: copied[key] ? '#28a745' : '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '12px',
        cursor: (!value || value === 'N/A') ? 'not-allowed' : 'pointer',
        padding: '4px 10px',
        marginLeft: 8,
        fontWeight: 500,
        transition: 'background-color 0.2s, opacity 0.2s',
        outline: 'none',
        position: 'relative',
        minWidth: 48,
        opacity: hovered[key] ? 1 : 0,
        pointerEvents: hovered[key] ? 'auto' : 'none',
        display: 'inline-block',
      }}
    >
      {copied[key] ? 'Copied!' : 'Copy'}
    </button>
  );

  const hoverHandlers = key => ({
    onMouseEnter: () => setHovered(prev => ({ ...prev, [key]: true })),
    onMouseLeave: () => setHovered(prev => ({ ...prev, [key]: false })),
  });

  return (
    <div style={{ marginTop: 16 }}>
      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', fontSize: 15 }}>Loading address and digipin...</div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ flex: 1, minWidth: 180, background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontWeight: 'bold', color: '#007bff', marginBottom: 8 }}>{fromLabel}</div>
            <div style={{ fontSize: 14, marginBottom: 8 }}>
              <div><strong>Address:</strong></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', alignItems: 'center', marginTop: 2 }} {...hoverHandlers(`${fromLabel}-address`)}>
                <span>{from?.address || 'N/A'}</span>
                {renderCopy(`${fromLabel}-address`, from?.address)}
              </div>
            </div>
            <div style={{ fontSize: 14, marginBottom: 8 }}>
              <div><strong>Digipin:</strong></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', alignItems: 'center', marginTop: 2 }} {...hoverHandlers(`${fromLabel}-digipin`)}>
                <span>{!isLocationInIndiaParam ? 'N/A' : (from?.digipin || 'N/A')}</span>
                {renderCopy(`${fromLabel}-digipin`, !isLocationInIndiaParam ? 'N/A' : from?.digipin)}
              </div>
            </div>
            <div style={{ fontSize: 14 }}>
              <div><strong>Lat Lng:</strong></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', alignItems: 'center', marginTop: 2 }} {...hoverHandlers(`${fromLabel}-latlng`)}>
                <span>{from?.lat && from?.lng ? `${from.lat}, ${from.lng}` : 'N/A'}</span>
                {renderCopy(`${fromLabel}-latlng`, from?.lat && from?.lng ? `${from.lat}, ${from.lng}` : '')}
              </div>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 180, background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontWeight: 'bold', color: '#28a745', marginBottom: 8 }}>{toLabel}</div>
            <div style={{ fontSize: 14, marginBottom: 8 }}>
              <div><strong>Address:</strong></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', alignItems: 'center', marginTop: 2 }} {...hoverHandlers(`${toLabel}-address`)}>
                <span>{to?.address || 'N/A'}</span>
                {renderCopy(`${toLabel}-address`, to?.address)}
              </div>
            </div>
            <div style={{ fontSize: 14, marginBottom: 8 }}>
              <div><strong>Digipin:</strong></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', alignItems: 'center', marginTop: 2 }} {...hoverHandlers(`${toLabel}-digipin`)}>
                <span>{!isLocationInIndiaParam ? 'N/A' : (to?.digipin || 'N/A')}</span>
                {renderCopy(`${toLabel}-digipin`, !isLocationInIndiaParam ? 'N/A' : to?.digipin)}
              </div>
            </div>
            <div style={{ fontSize: 14 }}>
              <div><strong>Lat Lng:</strong></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', alignItems: 'center', marginTop: 2 }} {...hoverHandlers(`${toLabel}-latlng`)}>
                <span>{to?.lat && to?.lng ? `${to.lat}, ${to.lng}` : 'N/A'}</span>
                {renderCopy(`${toLabel}-latlng`, to?.lat && to?.lng ? `${to.lat}, ${to.lng}` : '')}
              </div>
            </div>
          </div>
        </div>
      )}
      {!loading && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: 180, background: '#e3f2fd', borderRadius: 8, padding: 16, fontWeight: 'bold', fontSize: 18, color: '#007bff', textAlign: 'center' }}>
            Road Distance<br/>
            <span style={{ fontSize: 22 }}>{roadDistance ? `${parseFloat(roadDistance).toFixed(2)} km` : 'N/A'}</span>
            <div style={{ fontSize: 14, color: '#555', marginTop: 4 }}>{roadDuration ? `(${roadDuration})` : ''}</div>
          </div>
          <div style={{ flex: 1, minWidth: 180, background: '#fff3cd', borderRadius: 8, padding: 16, fontWeight: 'bold', fontSize: 18, color: '#856404', textAlign: 'center' }}>
            Aerial Distance<br/>
            <span style={{ fontSize: 22 }}>{aerialDistance ? `${parseFloat(aerialDistance).toFixed(2)} km` : 'N/A'}</span>
          </div>
        </div>
      )}
      {!loading && onGeneratePDF && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <PdfButton
            onClick={handleGeneratePDF}
            loading={pdfLoading}
            size="medium"
            title="Generate PDF with route information"
          />
        </div>
      )}
    </div>
  );
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  flex: '1 1 0%'
};

const mapCenter = {
  lat: 20.5937,
  lng: 78.9629
};

const Pin2PointMain = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [mapUrl, setMapUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputHistory, setInputHistory] = useState([]);
  const [usageHistory, setUsageHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputType, setInputType] = useState(''); // 'address' or 'digipin'
  const [copied, setCopied] = useState({});
  const [mapMarker, setMapMarker] = useState(null);
  const [searchMarker, setSearchMarker] = useState(null);
  const [mapMarkers, setMapMarkers] = useState([]);
  const mapSearchRef = useRef(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [mapType, setMapType] = useState('roadmap');
  const [satelliteLabels, setSatelliteLabels] = useState(true);
  const [placeId, setPlaceId] = useState(null); // Store place_id for API calls

  
  // User state
  const [user, setUser] = useState(null);
  
  // Route Optimizer functionality states
  const [distances, setDistances] = useState([]);
  const [newAddressCoords, setNewAddressCoords] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeCalculationType, setRouteCalculationType] = useState('nearest'); // 'nearest' or 'specific'
  const [routeError, setRouteError] = useState('');
  const [directions, setDirections] = useState(null);
  const [mapKey, setMapKey] = useState(0); // Key to force re-render of InteractiveMap

  const [dedupRectangles, setDedupRectangles] = useState([]); // Pure state for rectangles
  const [dedupRectanglesKey, setDedupRectanglesKey] = useState(0); // Force re-render when clearing

  const [routeAddresses, setRouteAddresses] = useState(null);
  const [mapClickLoading, setMapClickLoading] = useState(false);
  const [showMyLocations, setShowMyLocations] = useState(false);
  
  // Manual distance calculation states
  const [manualLoc1, setManualLoc1] = useState("");
  const [manualLoc2, setManualLoc2] = useState("");
  const [manualResult, setManualResult] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualLocationData, setManualLocationData] = useState({ loc1: null, loc2: null });
  
  // Single distance calculation states
  const [singleLoc, setSingleLoc] = useState("");
  const [selectedPreAddr, setSelectedPreAddr] = useState("");
  const [selectedPreAddrData, setSelectedPreAddrData] = useState(null);
  const [singleResult, setSingleResult] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleError, setSingleError] = useState("");
  const [singleLocationData, setSingleLocationData] = useState(null);
  const [boundsKey, setBoundsKey] = useState(0); // Add this to force bounds recalculation
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [preAddrSearch, setPreAddrSearch] = useState("");
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Use PDF generation hook
  const { generatePDF, generateUnifiedPDF, generateDigipinPDF, createSearchRecord, createDigipinSearchRecord, isLoading, error: pdfError } = usePdfGeneration();
  
  // Use the new address management hook
  const {
    addresses,
    loading: addressesLoading,
    error: addressesError,
    refreshAddresses: contextRefreshAddresses
  } = useAddresses();
  
  // Wrapper function to refresh both addresses and default location
  const refreshAddressesAndDefault = async () => {
    await contextRefreshAddresses();
    await fetchDefaultSavedLocation();
  };
  
  // Use shared location detection hook
  const {
    userLocation,
    isLocationInIndiaState,
    locationLoading,
    locationSource,
    showLocationMessage,
    defaultMapCenter,
    setShowLocationMessage,
    handleLocationPermissionGranted,
    handleLocationPermissionDenied,
    handleSetDefaultLocation
  } = useLocationDetection();

  // Recent searches dropdown states
  const [showRecentSearchesDropdown, setShowRecentSearchesDropdown] = useState(false);
  const [highlightedRecentIndex, setHighlightedRecentIndex] = useState(-1);
  const [recentSearchTerm, setRecentSearchTerm] = useState("");
  const mainSearchRef = useRef(null);
  const recentDropdownRef = useRef(null);

  // Modal states for location management
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  
  // Form states for location management
  const [formData, setFormData] = useState({
    input: '',
    label: '',
    inputType: 'address',
    place_id: null,
    setAsDefault: false
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');

  // New UI state variables
  const [showDropdown, setShowDropdown] = useState(false);

  // Specific Location functionality states
  const [showSpecificLocationField, setShowSpecificLocationField] = useState(false);
  const [specificLocationSearch, setSpecificLocationSearch] = useState("");
  const [specificLocationDropdownOpen, setSpecificLocationDropdownOpen] = useState(false);
  const [highlightedSpecificIndex, setHighlightedSpecificIndex] = useState(-1);
  const specificLocationRef = useRef(null);
  const specificDropdownRef = useRef(null);
  
  // Default saved location state (for pre-populating search field)
  const [defaultSavedLocation, setDefaultSavedLocation] = useState(null);

  // De-Dup functionality states
  const [enableDedup, setEnableDedup] = useState(false);
  const [dedupLevel, setDedupLevel] = useState(10);
  const [addressSimilarityThreshold, setAddressSimilarityThreshold] = useState(80);
  const [dedupData, setDedupData] = useState(null);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupMarkers, setDedupMarkers] = useState([]);
  const [selectedDedupGroup, setSelectedDedupGroup] = useState(null);
  const [showDedupModal, setShowDedupModal] = useState(false);

  // Fetch default saved location to pre-populate search field
  const fetchDefaultSavedLocation = async () => {
    try {
      const response = await authenticatedFetch('/addresses/default');
      if (response.ok) {
        const data = await response.json();
        setDefaultSavedLocation(data.default_address);
        
        // If there's a default saved location, pre-populate the search field
        if (data.default_address) {
          setSpecificLocationSearch(data.default_address.label || data.default_address.address);
        }
      }
    } catch (error) {
      console.error('Error fetching default saved location:', error);
    }
  };



  // Haversine formula to calculate distance between two points
  const calculateHaversineDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in kilometers
  };

  // Calculate midpoint between two coordinates
  const calculateMidpoint = (lat1, lng1, lat2, lng2) => {
    const midLat = (lat1 + lat2) / 2;
    const midLng = (lng1 + lng2) / 2;
    return { lat: midLat, lng: midLng };
  };

  // Generate route distance info for display on map
  const getRouteDistanceInfo = () => {
    if (distances.length === 0) return null;
    
    const nearestLocation = distances[0];
    if (!nearestLocation || !nearestLocation.lat || !nearestLocation.lng) return null;
    
    // Determine which location data to use (priority: result > searchMarker > userLocation)
    let currentLocation = null;
    
    if (result && result.lat && result.lng) {
      currentLocation = result;
    } else if (searchMarker && searchMarker.lat && searchMarker.lng) {
      currentLocation = searchMarker;
    } else if (userLocation && userLocation.lat && userLocation.lng) {
      currentLocation = userLocation;
    } else {
      return null;
    }
    
    const midpoint = calculateMidpoint(
      currentLocation.lat, currentLocation.lng,
      nearestLocation.lat, nearestLocation.lng
    );
    
    return {
      position: midpoint,
      roadDistance: nearestLocation.road_distance_km,
      aerialDistance: nearestLocation.aerial_distance_km,
      roadDuration: nearestLocation.road_duration
    };
  };

  // Calculate nearest location using mathematical formula
  const calculateNearestLocationMathematical = async () => {
    // Determine which location data to use (priority: result > mapMarker > searchMarker > userLocation)
    let currentLocation = null;
    
    if (result && result.lat && result.lng) {
      currentLocation = result;
    } else if (mapMarker && mapMarker.lat && mapMarker.lng) {
      currentLocation = mapMarker;
    } else if (searchMarker && searchMarker.lat && searchMarker.lng) {
      currentLocation = searchMarker;
    } else if (userLocation && userLocation.lat && userLocation.lng) {
      currentLocation = userLocation;
    } else {
      setRouteError('Please get a location first by searching, clicking on the map, or allowing location access.');
      return;
    }
    
    console.log('Using location data for nearest location calculation:', {
      source: result ? 'map_click' : mapMarker ? 'map_click' : searchMarker ? 'search' : 'user_location',
      currentLocation: {
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        address: currentLocation.address
      }
    });

    setDistances([]);
    setNewAddressCoords(null);
    clearRoute();

    try {
      // Use addresses from the context
      if (addresses.length === 0) {
        setRouteError('No predefined addresses found. Please add some in your profile.');
        return;
      }
      
      // First calculate aerial distances using mathematical formula to find the nearest locations
      const calculatedDistances = addresses.map(addr => {
        const aerialDistance = calculateHaversineDistance(
          currentLocation.lat, currentLocation.lng,
          parseFloat(addr.lat), parseFloat(addr.lng)
        );
        return {
          address: addr.address,
          label: addr.label,
          aerial_distance_km: aerialDistance,
          lat: addr.lat,
          lng: addr.lng,
          digipin: addr.digipin
        };
      });

      // Sort by aerial distance to get the nearest locations
      calculatedDistances.sort((a, b) => a.aerial_distance_km - b.aerial_distance_km);
      
      // Get top 3 locations with shortest aerial distances
      const top3Locations = calculatedDistances.slice(0, Math.min(3, calculatedDistances.length));
      
      // Calculate road distance for all top 3 locations
      const location1 = `${currentLocation.lat},${currentLocation.lng}`; // Current location coordinates
      const roadDistancePromises = top3Locations.map(location => 
        authenticatedFetch('/distance-between', {
          method: 'POST',
          body: JSON.stringify({ 
            location1: location1, 
            location2: location.address 
          }),
        }).then(async response => {
          if (response.ok) {
            const data = await response.json();
            return {
              ...location,
              road_distance_km: data.road_distance_km,
              road_duration: data.duration
            };
          } else {
            return {
              ...location,
              road_distance_km: null,
              road_duration: null
            };
          }
        }).catch(() => ({
          ...location,
          road_distance_km: null,
          road_duration: null
        }))
      );
      
      const locationsWithRoadDistances = await Promise.all(roadDistancePromises);
      
      // Find the location with shortest road distance
      const validLocations = locationsWithRoadDistances.filter(loc => loc.road_distance_km !== null);
      let nearestLocation = null;
      
      if (validLocations.length > 0) {
        // Sort by road distance and pick the shortest
        validLocations.sort((a, b) => a.road_distance_km - b.road_distance_km);
        nearestLocation = validLocations[0];
      } else {
        // Fallback to first location by aerial distance if no road distances available
        nearestLocation = top3Locations[0];
        nearestLocation.road_distance_km = null;
        nearestLocation.road_duration = null;
      }
      
      // Create the final result with both aerial and road distances
      const finalResult = {
        address: nearestLocation.address,
        label: nearestLocation.label,
        aerial_distance_km: nearestLocation.aerial_distance_km,
        road_distance_km: nearestLocation.road_distance_km,
        road_duration: nearestLocation.road_duration,
        lat: nearestLocation.lat,
        lng: nearestLocation.lng,
        digipin: nearestLocation.digipin
      };
      
      // Set only the nearest location result
      setDistances([finalResult]);
      
      // Update the "Search my location" input to show the nearest location
      setSpecificLocationSearch(nearestLocation.label || nearestLocation.address);
      
      // Set the current location coordinates
      setNewAddressCoords({
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        address: currentLocation.address,
        digipin: currentLocation.digipin
      });

      // Calculate route to the nearest address
      const origin = `${currentLocation.lat},${currentLocation.lng}`;
      const destination = `${nearestLocation.lat},${nearestLocation.lng}`;
      
      const routeAddresses = {
        origin: {
          address: currentLocation.address,
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          digipin: currentLocation.digipin
        },
        destination: {
          address: nearestLocation.address,
          lat: nearestLocation.lat,
          lng: nearestLocation.lng,
          digipin: nearestLocation.digipin
        }
      };
      
      // Add destination marker to mapMarkers so it's always visible
      setMapMarkers([{ 
        lat: parseFloat(nearestLocation.lat), 
        lng: parseFloat(nearestLocation.lng),
        label: nearestLocation.label || nearestLocation.address,
        isRouteMarker: true,
        digipin: nearestLocation.digipin
      }]);
      
      await calculateRoute(
        `${routeAddresses.origin.lat},${routeAddresses.origin.lng}`,
        `${routeAddresses.destination.lat},${routeAddresses.destination.lng}`,
        routeAddresses
      );
    } catch (error) {
      setRouteError('Error calculating distances. Please try again.');
      throw error; // Re-throw so handleNearestLocation can catch it
    }
  };

  useEffect(() => {
    fetchInputHistory();
    
    // Fetch user profile
    const fetchUserProfile = async () => {
      try {
        const profile = await getUserProfile();
        if (profile && profile.email) {
          setUserEmail(profile.email);
          setUser(profile);
        }
      } catch (error) {
        // Error handling without console.log
      }
    };
    fetchUserProfile();

    // Fetch user's map preference
    const fetchMapPreference = async () => {
      try {
        const preference = await getUserMapPreference();
        setMapType(preference.map_view_preference);
        setSatelliteLabels(preference.satellite_labels);
      } catch (error) {
        // Error handling without console.log
      }
    };
    fetchMapPreference();
    
    // Fetch user's preferences for dedup and digipin level
    const fetchUserPreferences = async () => {
      try {
        const response = await authenticatedFetch('/user-preferences');
        if (response.ok) {
          const preferences = await response.json();
          console.log('Fetched user preferences:', preferences);
          setEnableDedup(preferences.default_dedup_enabled);
          setDedupLevel(preferences.default_digipin_level);
        }
      } catch (error) {
        console.error('Error fetching user preferences:', error);
      }
    };
    fetchUserPreferences();
    
    // Fetch addresses
    refreshAddressesAndDefault();
    
    // Fetch default saved location to pre-populate search field
    fetchDefaultSavedLocation();
    

  }, []);

  // Fetch De-Dup data
  const fetchDedupData = async () => {
    console.log('fetchDedupData called with enableDedup:', enableDedup, 'dedupLevel:', dedupLevel);

    
    // Clear rectangles immediately using pure state
    setDedupRectangles([]);
    setDedupRectanglesKey(prev => prev + 1);
    
    // Clear existing data
    setDedupData(null);
    setDedupMarkers([]);
    setSelectedDedupGroup(null);
    setShowDedupModal(false);
    
    if (!enableDedup) {
      console.log('Dedup is disabled, returning early');
      return;
    }

    // Get the current search's DigiPin as reference
    let referenceDigipin = null;
    
    if (result && result.digipin) {
      referenceDigipin = result.digipin;
    } else if (searchMarker && searchMarker.digipin) {
      referenceDigipin = searchMarker.digipin;
    } else if (mapMarker && mapMarker.digipin) {
      referenceDigipin = mapMarker.digipin;
    } else if (result && result.lat && result.lng) {
      // Fallback: Generate DigiPin from coordinates if available
      try {
        const response = await authenticatedFetch('/generate-digipin', {
          method: 'POST',
          body: JSON.stringify({ 
            lat: result.lat, 
            lng: result.lng
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          referenceDigipin = data.digipin;
        }
      } catch (error) {
        console.error('Error generating DigiPin for De-Dup:', error);
      }
    }

    // If no reference DigiPin available, don't fetch data
    if (!referenceDigipin) {
      setDedupData(null);
      setDedupMarkers([]);
      return;
    }

    // Get the current search's address as reference for similarity matching
    let referenceAddress = null;
    if (result && result.address) {
      referenceAddress = result.address;
    } else if (searchMarker && searchMarker.address) {
      referenceAddress = searchMarker.address;
    } else if (mapMarker && mapMarker.address) {
      referenceAddress = mapMarker.address;
    }

    setDedupLoading(true);
    try {
      // Build query string with parameters
      const queryParams = new URLSearchParams({
        level: dedupLevel.toString(),
        reference_digipin: encodeURIComponent(referenceDigipin)
      });
      
      // Add address filtering if enabled (threshold > 0) and reference address available
      if (addressSimilarityThreshold > 0 && referenceAddress) {
        queryParams.append('reference_address', encodeURIComponent(referenceAddress));
        queryParams.append('address_similarity_threshold', addressSimilarityThreshold.toString());
        console.log('Fetching De-Dup data with address filtering:', { 
          level: dedupLevel, 
          referenceDigipin,
          referenceAddress,
          addressSimilarityThreshold 
        });
      } else {
        console.log('Fetching De-Dup data with:', { level: dedupLevel, referenceDigipin });
      }
      
      const response = await authenticatedFetch(`/dedup-data?${queryParams.toString()}`);
      
      console.log('De-Dup API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('De-Dup API response data:', data);
        setDedupData(data);
        
        // Convert groups to map markers
        const markers = data.groups
          .filter(group => group.center_lat && group.center_lng) // Only include groups with valid coordinates
          .map(group => ({
            lat: group.center_lat,
            lng: group.center_lng,
            count: group.count,
            digipin_prefix: group.digipin_prefix,
            groupData: group,
            bounds: group.bounds,
            dedupLevel: dedupLevel,
            type: 'dedup'
          }));
        console.log('De-Dup markers created:', markers.map(marker => ({
          lat: marker.lat,
          lng: marker.lng,
          count: marker.count,
          digipin_prefix: marker.digipin_prefix,
          bounds: marker.bounds
        })));
        setDedupMarkers(markers);
        
        // Set single rectangle in pure state (should only be one now)
        const rectangles = data.groups
          .filter(group => group.bounds) // Only include groups with valid bounds
          .map(group => ({
            bounds: group.bounds,
            level: dedupLevel,
            count: group.count,
            digipin_prefix: group.digipin_prefix,
            center_lat: group.center_lat,
            center_lng: group.center_lng
          }));
        console.log('Setting single rectangle:', rectangles);
        setDedupRectangles(rectangles);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch de-duplication data:', response.status, errorText);
        setDedupData(null);
        setDedupMarkers([]);
        setDedupRectangles([]);
      }
    } catch (error) {
      console.error('Error fetching de-duplication data:', error);
      setDedupData(null);
      setDedupMarkers([]);
      setDedupRectangles([]);
    } finally {
      setDedupLoading(false);
    }
  };

  // Handle De-Dup marker click
  const handleDedupMarkerClick = (marker) => {
    setSelectedDedupGroup(marker.groupData);
    setShowDedupModal(true);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires');
    window.location.href = '/';
  };

  // Click outside handlers for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('[data-dropdown]')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Generate digipin for search results
  const generateDigipinForSearch = async (lat, lng, address, isAddressSearch = false, originalInput = null) => {
    try {
      // Build request body - only include address if it's a real address (not coordinates)
      const requestBody = {
        lat,
        lng,
        is_address_search: isAddressSearch
      };
      
      // Use original user input if available (what they typed), otherwise use the geocoded address
      // For saving purposes, we prefer the original input over the formatted address
      const addressToSave = originalInput && originalInput !== `${lat},${lng}` ? originalInput : 
                           (address && address !== `${lat},${lng}` ? address : null);
      
      // Only add address if it exists and is not just coordinates
      if (addressToSave) {
        requestBody.address = addressToSave;
      }
      
      console.log('=== generateDigipinForSearch DEBUG ===');
      console.log('Sending to /generate-digipin:', requestBody);
      console.log('isAddressSearch:', isAddressSearch);
      console.log('formatted address (for display):', address);
      console.log('original input (for saving):', originalInput);
      console.log('addressToSave:', addressToSave);
      
      const response = await authenticatedFetch('/generate-digipin', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Clear existing routes and markers when a new search result is generated
        clearRoute();
        setMapMarkers([]);
        setDistances([]);
        setNewAddressCoords(null);
        setRouteError('');
        
        // Clear specific location dropdown but keep the field visible
        setSpecificLocationDropdownOpen(false);
        
        // Clear De-Dup data when generating new search results
        setDedupData(null);
        setDedupMarkers([]);
        setDedupRectangles([]);
        setSelectedDedupGroup(null);
        setShowDedupModal(false);
        // Don't disable De-Dup - keep user's preference
        
        // Create enhanced search marker with digipin
        const enhancedSearchMarker = {
          lat: lat,
          lng: lng,
          address: address || `${lat},${lng}`,  // Use provided address, fallback to coordinates
          digipin: data.digipin || 'N/A'
        };
        
        setSearchMarker(enhancedSearchMarker);
        setLatitude(lat);
        setLongitude(lng);
        setMapUrl(`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`);
        
        // If dedup is enabled, fetch dedup data for the new location
        if (enableDedup) {
          console.log('Dedup is enabled, will fetch dedup data for new location');
          // Use setTimeout to ensure state updates are complete before fetching
          setTimeout(() => {
            fetchDedupData();
          }, 100);
        } else {
          console.log('Dedup is disabled, skipping dedup data fetch');
        }
      } else {
        // Fallback if digipin generation fails
        setSearchMarker({ lat, lng, address: address || `${lat},${lng}`, digipin: 'N/A' });
        setLatitude(lat);
        setLongitude(lng);
        setMapUrl(`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`);
        
        // If dedup is enabled, fetch dedup data for the new location
        if (enableDedup) {
          console.log('Dedup is enabled, will fetch dedup data for new location');
          // Use setTimeout to ensure state updates are complete before fetching
          setTimeout(() => {
            fetchDedupData();
          }, 100);
        } else {
          console.log('Dedup is disabled, skipping dedup data fetch');
        }
      }
    } catch (error) {
      console.error('Error generating digipin for search:', error);
      // Fallback if API call fails
      setSearchMarker({ lat, lng, address: address || `${lat},${lng}`, digipin: 'N/A' });
      setLatitude(lat);
      setLongitude(lng);
      setMapUrl(`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`);
      
      // If dedup is enabled, fetch dedup data for the new location
      if (enableDedup) {
        console.log('Dedup is enabled, will fetch dedup data for new location');
        // Use setTimeout to ensure state updates are complete before fetching
        setTimeout(() => {
          fetchDedupData();
        }, 100);
      } else {
        console.log('Dedup is disabled, skipping dedup data fetch');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate digipin from coordinates by calling the backend
  const generateDigipinFromCoordinates = async (lat, lng) => {
    try {
      const response = await authenticatedFetch('/generate-digipin', {
        method: 'POST',
        body: JSON.stringify({ lat, lng }),
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.digipin || '';
      } else {
        console.error('Failed to generate digipin from coordinates');
        return '';
      }
    } catch (error) {
      console.error('Error generating digipin from coordinates:', error);
      return '';
    }
  };

  // Log POI click usage
  const logPoiClickUsage = async (placeId, placeName, formattedAddress, lat, lng, digipin) => {
    try {
      await authenticatedFetch('/log-usage', {
        method: 'POST',
        body: JSON.stringify({
          search_input: `${placeName}, ${formattedAddress}`,
          address: `${placeName}, ${formattedAddress}`,
          digipin: digipin,
          latitude: lat,
          longitude: lng,
          place_id: placeId,
          search_type: 'poi_click'
        }),
      });
    } catch (error) {
      console.error('Error logging POI click usage:', error);
    }
  };

  // Handle API response for map clicks
  const handleApiResponse = (data, lat, lng, fullLocationData) => {
    console.log('handleApiResponse called with:', { data, lat, lng, fullLocationData });
    
    // Clear existing routes and markers when a new location is selected
    clearRoute();
    setMapMarkers([]);
    setDistances([]);
    setNewAddressCoords(null);
    setRouteError('');
    
    // Clear specific location dropdown but keep the field visible
    setSpecificLocationDropdownOpen(false);
    
    // Enhanced result object with complete place data
    const enhancedResult = {
      ...data,
      // If we have displayName and formattedAddress from the backend, use them
      place_name: data.displayName?.text || data.place_name || '',
      formatted_address: data.formattedAddress || data.formatted_address || '',
      place_types: data.place_types || [],
      photos: data.photos || []
    };
    
    console.log('Setting enhanced result:', enhancedResult);
    setResult(enhancedResult);
    setLatitude(data.lat || lat);
    setLongitude(data.lng || lng);
    
    const mapMarkerData = { 
      lat: data.lat || lat, 
      lng: data.lng || lng,
      address: data.address || enhancedResult.address,
      digipin: data.digipin || enhancedResult.digipin
    };
    console.log('Setting map marker:', mapMarkerData);
    setMapMarker(mapMarkerData);
    
    setSearchMarker(null);
    setMapUrl(`https://maps.google.com/maps?q=${data.lat || lat},${data.lng || lng}&z=15&output=embed`);
    setError('');
    
    // Update input field with the retrieved address
    if (data.address) {
      setInput(data.address);
    }
    
    // If dedup is enabled, fetch dedup data for the new location
    if (enableDedup) {
      console.log('Dedup is enabled, will fetch dedup data for new location');
      // Use setTimeout to ensure state updates are complete before fetching
      setTimeout(() => {
        fetchDedupData();
      }, 100);
    } else {
      console.log('Dedup is disabled, skipping dedup data fetch');
    }
  };

  const fetchInputHistory = async () => {
    try {
      const response = await authenticatedFetch('/usage-history');
      if (response.ok) {
        const data = await response.json();
        setUsageHistory(data);
        
        // Get unique search records from history, sorted by most recent first
        const inputRecords = data
          .filter(record => (record.address && record.address.trim() !== '') || 
                           (record.digipin && record.digipin.trim() !== ''))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        // Create unique search entries based on the search input (not the result)
        const uniqueInputs = [];
        const seenInputs = new Set();
        
        for (const record of inputRecords) {
          // Determine what was the original search input
          let searchInput = '';
          let searchType = '';
          
          // If this was a reverse geocode (lat/lng search), use the coordinates as input
          if (record.latitude && record.longitude && record.source === 'combined_digipin') {
            searchInput = `${record.latitude},${record.longitude}`;
            searchType = 'latlong';
          }
          // If this was an address search, use the address as input
          else if (record.address && record.action === 'reverse') {
            searchInput = record.address;
            searchType = 'address';
          }
          // If this was a digipin search, use the digipin as input
          else if (record.digipin && record.action === 'lookup') {
            searchInput = record.digipin;
            searchType = 'digipin';
          }
          // Fallback: use address if available, otherwise digipin
          else if (record.address) {
            searchInput = record.address;
            searchType = 'address';
          } else if (record.digipin) {
            searchInput = record.digipin;
            searchType = 'digipin';
          }
          
          if (searchInput && !seenInputs.has(`${searchType}:${searchInput}`)) {
            uniqueInputs.push({ value: searchInput, type: searchType });
            seenInputs.add(`${searchType}:${searchInput}`);
          }
          
          if (uniqueInputs.length >= 10) break;
        }
        
        setInputHistory(uniqueInputs);
      }
    } catch (error) {
      // Error handling without console.log
    }
  };

  // New function to handle search without API call - just create marker
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    // Store the current input value to keep it after reset
    const currentInput = input.trim();
    
    // Reset page completely but keep the entered address
    setLoading(true);
    setError('');
    setResult(''); // Clear previous results
    setMapMarker(null);
    setMapUrl('');
    // Don't clear searchMarker immediately - keep current map position
    clearRoute();
    setMapMarkers([]); // Clear all route markers
    setDistances([]);
    setNewAddressCoords(null);
    setRouteError('');
    setManualResult(null);
    setSingleResult(null);
    
    // Clear De-Dup data when starting a new search
    setDedupData(null);
    setDedupMarkers([]);
    setSelectedDedupGroup(null);
    setShowDedupModal(false);
    // Don't disable De-Dup - keep user's preference
    
    // Keep the entered address in the input field
    setInput(currentInput);
    
    try {
      // Determine input type
      const inputType = detectInputType(input);
      setInputType(inputType);
      
      if (inputType === 'latlng') {
        // For lat/lng input, generate digipin and create marker
        const coords = parseLatLng(input);
        generateDigipinForSearch(coords.lat, coords.lng, `${coords.lat},${coords.lng}`);
      } else {
        // For address or digipin, use Google Geocoding API (client-side only)
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          
          let geocodeRequest;
          if (inputType === 'digipin') {
            // For digipin, make API call to decode it
            try {
              const response = await authenticatedFetch('/get-address', {
                method: 'POST',
                body: JSON.stringify({ digipin: input.trim() }),
              });
              
              if (response.ok) {
                const data = await response.json();
                // Generate digipin for the search result
                generateDigipinForSearch(data.lat, data.lng, data.address);
              } else {
                const errorData = await response.json();
                setError(handleApiError(errorData, 'Failed to decode Digipin.'));
                setLoading(false);
              }
            } catch (error) {
              setError('Network error. Please try again.');
              setLoading(false);
            }
            return;
          } else {
            // For address, geocode it - append ", India" if not already present
            let addressToGeocode = input.trim();
            const lowerAddress = addressToGeocode.toLowerCase();
            // Check if address already ends with "India" or ", India"
            if (!lowerAddress.endsWith('india') && !lowerAddress.endsWith(', india')) {
              addressToGeocode = `${addressToGeocode}, India`;
            }
            geocodeRequest = { address: addressToGeocode };
          }
          
          geocoder.geocode(geocodeRequest, (results, status) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              const lat = location.lat();
              const lng = location.lng();
              
              // Generate digipin for the search result
              // Pass the original user input (currentInput) instead of formatted address
              // formatted_address is still passed for display purposes, but originalInput is used for saving
              generateDigipinForSearch(lat, lng, results[0].formatted_address, true, currentInput);
            } else {
              setError('Could not find location. Please try a different search term or click on the map.');
              setLoading(false);
            }
          });
        } else {
          setError('Google Maps not loaded. Please refresh the page.');
          setLoading(false);
        }
      }
    } catch (error) {
      setError('Error processing search. Please try again.');
      setLoading(false);
    }
  };



  // Function to handle map clicks - this will call the backend API
  const handleMapClick = async (clickData) => {
    console.log('handleMapClick received:', clickData);
    const { lat, lng, placeId, placeName, formattedAddress, placeTypes, photos } = clickData;
    
    console.log('Extracted POI data:', { placeId, placeName, formattedAddress, placeTypes });
    
    console.log('Current result state before map click:', result);
    
    // Reset map completely - clear all previous markers, routes, and information
    setMapMarker(null);
    setSearchMarker(null);
    clearRoute();
    setMapMarkers([]); // Clear all route markers
    
    // Clear previous distance calculation results and errors
    setDistances([]);
    setNewAddressCoords(null);
    setRouteError('');
    setManualResult(null);
    setSingleResult(null);
    
    // Clear specific location dropdown but keep the field visible
    setSpecificLocationDropdownOpen(false);
    
    // Clear De-Dup data when clicking on map
    setDedupData(null);
    setDedupMarkers([]);
    setDedupRectangles([]);
    setSelectedDedupGroup(null);
    setShowDedupModal(false);
    // Don't disable De-Dup - keep user's preference
    
    // Show loading indicator immediately for instant visual feedback
    setMapClickLoading(true);
    setError('');
    
    try {
      // Use Places API data if available
      let locationText = `${lat},${lng}`;
      let fullLocationData = { lat, lng, address: '', digipin: '', place_name: '' };
      
      if (placeId) {
        console.log('Places API data received:', { placeId, placeName, formattedAddress, placeTypes });
        console.log('placeName truthy check:', !!placeName);
        console.log('formattedAddress truthy check:', !!formattedAddress);
        
        // Use the new simple digipin generation endpoint
        const response = await authenticatedFetch('/generate-digipin', {
          method: 'POST',
          body: JSON.stringify({
            lat,
            lng,
            place_id: placeId,
            place_name: placeName
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Create complete result object using frontend data + backend digipin
          // Handle cases where placeName or formattedAddress might be missing
          let address = '';
          if (placeName && formattedAddress) {
            address = `${placeName}, ${formattedAddress}`;
          } else if (placeName) {
            address = placeName;
          } else if (formattedAddress) {
            address = formattedAddress;
          } else {
            address = `${lat},${lng}`; // Fallback to coordinates
          }
          
          const completeResult = {
            address: address,
            digipin: data.digipin,
            lat: lat,
            lng: lng,
            place_id: placeId,
            place_name: placeName || '',
            formatted_address: formattedAddress || '',
            place_types: placeTypes || [],
            photos: photos || []
          };
          
          // Use the existing handleApiResponse function
          handleApiResponse(completeResult, lat, lng, completeResult);
        } else {
          setError('Failed to generate digipin');
        }
        
      } else {
        // No place data available - use coordinates as address (no reverse geocoding)
        console.log('No place data - using coordinates as address');
        
        // Use coordinates as address, no reverse geocoding
        locationText = `${lat},${lng}`;
        fullLocationData = { lat, lng, address: `${lat},${lng}`, digipin: '', place_name: '' };
        
        // Generate digipin without requesting address
        const response = await authenticatedFetch('/generate-digipin', {
          method: 'POST',
          body: JSON.stringify({
            lat,
            lng
            // No get_address parameter - use existing data
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Create result object using coordinates as address
          const result = {
            address: `${lat},${lng}`,  // Use coordinates as address
            digipin: data.digipin,
            lat: lat,
            lng: lng,
            place_id: null
          };
          
          handleApiResponse(result, lat, lng, fullLocationData);
        } else {
          setError('Failed to generate digipin');
        }
      }
        
        // Set place_id for future API calls
        setPlaceId(fullLocationData.place_id);
        
        // Handle manual distance calculation if needed
        if (manualLocationData.loc1) {
          await handleManualDistanceCalculation(locationText, fullLocationData);
        }
        
        // Set single location data for route optimization
        setSingleLocationData({
          address: fullLocationData.address || locationText,
          lat: lat,
          lng: lng
        });
        
        fetchInputHistory();
        
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setMapClickLoading(false);
    }
  };

  const handleInputClick = async (clickedInput) => {
    // Ensure clickedInput is an object with a value property
    if (!clickedInput || typeof clickedInput !== 'object' || !clickedInput.value) {
      return;
    }
    
    // Ensure the value is a string
    const inputValue = typeof clickedInput.value === 'string' ? clickedInput.value : String(clickedInput.value);
    
    // Find the most recent record for this input from usage history
    const record = usageHistory
      .filter(r => (clickedInput.type === 'address' && r.address === clickedInput.value) ||
                   (clickedInput.type === 'digipin' && r.digipin === clickedInput.value) ||
                   (clickedInput.type === 'latlong' && r.latitude && r.longitude && 
                    `${r.latitude},${r.longitude}` === clickedInput.value))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
    
    if (record) {
      // Use existing data from the record
      const safeAddress = typeof record.address === 'string' ? record.address : '';
      const safeDigipin = typeof record.digipin === 'string' ? record.digipin : '';
      const safeLatitude = typeof record.latitude === 'string' ? parseFloat(record.latitude) : 0;
      const safeLongitude = typeof record.longitude === 'string' ? parseFloat(record.longitude) : 0;
      
      // Update the UI with existing data
      setInput(inputValue);
      setResult({
        address: safeAddress,
        digipin: safeDigipin,
        lat: safeLatitude,
        lng: safeLongitude
      });
      setLatitude(safeLatitude);
      setLongitude(safeLongitude);
      setMapMarker({ lat: safeLatitude, lng: safeLongitude });
      setSearchMarker(null);
      setMapUrl(`https://maps.google.com/maps?q=${safeLatitude},${safeLongitude}&z=15&output=embed`);
      setError('');
      setLoading(false);
      
      // Set single location data
      setSingleLocationData({
        address: safeAddress,
        lat: safeLatitude,
        lng: safeLongitude
      });
    } else {
      // Fallback to search without API call
      setResult('');
      setError('');
      setLatitude('');
      setLongitude('');
      setMapUrl('');
      setMapMarker(null);
      setSearchMarker(null);
      setInput(inputValue);
      // Don't call API here - just search for location
      handleSearch({ preventDefault: () => {} });
    }
  };

  const generateHistoryPDF = async (historyRecord, recordId) => {
    try {
      await generateUnifiedPDF(historyRecord, {
        type: 'single',
        userEmail
      }, recordId);
    } catch (error) {
      setError('Failed to generate PDF evidence');
    }
  };

  const filteredAddresses = addresses.filter(addr => {
    const searchTerm = preAddrSearch.toLowerCase();
    const addrLabel = (addr.label || '').toLowerCase();
    const addrAddress = (addr.address || '').toLowerCase();
    
    return addrLabel.includes(searchTerm) || addrAddress.includes(searchTerm);
  }).slice(0, 10);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectAddress = (addr) => {
    // Clear existing routes and markers when selecting a new address
    clearRoute();
    setMapMarkers([]);
    setDistances([]);
    setNewAddressCoords(null);
    setRouteError('');
    
    // Clear specific location dropdown but keep the field visible
    setSpecificLocationDropdownOpen(false);
    
    setSelectedPreAddr(addr.address);
    setSelectedPreAddrData(addr);
    setPreAddrSearch(addr.address);
    setDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const clearRoute = () => {
    setDirections(null);
    setRouteAddresses(null);
    setMapMarkers([]);
  };

  const calculateRoute = async (origin, destination, customAddresses = null) => {
    if (!origin || !destination) return;
    
    try {
      const request = {
        origin: origin,
        destination: destination,
        travelMode: window.google.maps.TravelMode.DRIVING
      };
      
      const directionsService = new window.google.maps.DirectionsService();
      const result = await directionsService.route(request);
      
      if (result.status === 'OK' && result.routes && result.routes.length > 0) {
        setDirections(result);
        if (customAddresses) {
          setRouteAddresses(customAddresses);
        }
      }
    } catch (error) {
      // Error handling without console.log
    }
  };

  const handleNearestLocation = async () => {
    // Set loading state for the button
    setRouteLoading(true);
    setRouteCalculationType('nearest');
    setRouteError('');
    
    // Clear existing routes and markers when calculating nearest location
    clearRoute();
    setMapMarkers([]);
    setDistances([]);
    setNewAddressCoords(null);
    
    // Clear specific location dropdown but keep the field visible
    setSpecificLocationDropdownOpen(false);
    
    try {
      // Use mathematical formula approach (faster, still provides accurate results)
      await calculateNearestLocationMathematical();
    } catch (error) {
      setRouteError('Error calculating nearest location. Please try again.');
    } finally {
      setRouteLoading(false);
    }
  };

  const handleManualDistanceCalculation = async (locationText, fullLocationData) => {
    if (!manualLocationData.loc1 || !fullLocationData) return;
    
    setManualLoading(true);
    setManualError('');
    
    try {
      const response = await authenticatedFetch('/manual-distance', {
        method: 'POST',
        body: JSON.stringify({
          location1: manualLocationData.loc1,
          location2: fullLocationData
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setManualResult(data);
        setManualError('');
        
        // Calculate route
        const origin = `${manualLocationData.loc1.lat},${manualLocationData.loc1.lng}`;
        const destination = `${fullLocationData.lat},${fullLocationData.lng}`;
        
        const routeAddresses = {
          origin: manualLocationData.loc1.address,
          destination: fullLocationData.address || locationText
        };
        
        await calculateRoute(
          `${routeAddresses.origin.lat},${routeAddresses.origin.lng}`,
          `${routeAddresses.destination.lat},${routeAddresses.destination.lng}`,
          routeAddresses
        );
      } else {
        const errorData = await response.json();
        setManualError(errorData.detail || 'Failed to calculate distance.');
      }
    } catch (err) {
      setManualError('An error occurred. Please try again.');
    } finally {
      setManualLoading(false);
    }
  };

  const handleSingleDistanceCalculation = async () => {
    if (!singleLocationData || !manualLocationData.loc1) {
      setManualError('Please select both locations first.');
      return;
    }
    
    setManualLoading(true);
    setManualError('');
    
    try {
      const response = await authenticatedFetch('/manual-distance', {
        method: 'POST',
        body: JSON.stringify({
          location1: manualLocationData.loc1,
          location2: singleLocationData
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSingleResult(data);
        setManualError('');
        
        // Calculate route
        const origin = `${manualLocationData.loc1.lat},${manualLocationData.loc1.lng}`;
        const destination = `${singleLocationData.lat},${singleLocationData.lng}`;
        
        const routeAddresses = {
          origin: manualLocationData.loc1.address,
          destination: singleLocationData.address
        };
        
        await calculateRoute(
          `${routeAddresses.origin.lat},${routeAddresses.origin.lng}`,
          `${routeAddresses.destination.lat},${routeAddresses.destination.lng}`,
          routeAddresses
        );
      } else {
        const errorData = await response.json();
        setManualError(errorData.detail || 'Failed to calculate distance.');
      }
    } catch (err) {
      setManualError('An error occurred. Please try again.');
    } finally {
      setManualLoading(false);
    }
  };

  const clearAll = () => {
    setInput('');
    setResult('');
    setLatitude('');
    setLongitude('');
    setMapUrl('');
    setError('');
    setMapMarker(null);
    setSearchMarker(null);
    setDistances([]);
    setNewAddressCoords(null);
    setRouteError('');
    setRouteLoading(false);
    setManualLoc1('');
    setManualLoc2('');
    setManualResult(null);
    setManualError('');
    setSingleLoc('');
    setSelectedPreAddr('');
    setSelectedPreAddrData(null);
    setSingleResult(null);
    setSingleError('');
    setMapMarkers([]);
    clearRoute();
    
    // Force re-render of InteractiveMap by changing the key
    setMapKey(prev => prev + 1);
    
    // Clear specific location dropdown but keep the field visible
    setSpecificLocationDropdownOpen(false);
    setHighlightedSpecificIndex(-1);
  };

  // Combine markers for display
  const allMarkers = [];
   
   // Determine the current marker (priority: result > mapMarker > searchMarker > userLocation)
   let currentMarker = null;
   console.log('Marker creation - checking result:', result);
   console.log('Marker creation - checking mapMarker:', mapMarker);
   console.log('Marker creation - checking searchMarker:', searchMarker);
   console.log('Marker creation - checking userLocation:', userLocation);
   
   if (result && result.lat && result.lng) {
     currentMarker = { 
       lat: result.lat, 
       lng: result.lng, 
       isCurrentMarker: true,
       title: "Selected Location",
       info: {
         address: result.address || 'N/A',
         digipin: result.digipin || 'N/A',
         coordinates: `${result.lat}, ${result.lng}`,
         lat: result.lat,
         lng: result.lng
       }
     };
     console.log('Created current marker from result:', currentMarker);
   } else if (mapMarker) {
     currentMarker = { 
       ...mapMarker, 
       isCurrentMarker: true,
       title: "Map Location",
       info: {
         address: mapMarker.address || 'Map Location',
         coordinates: `${mapMarker.lat}, ${mapMarker.lng}`,
         digipin: mapMarker.digipin || 'N/A',
         lat: mapMarker.lat,
         lng: mapMarker.lng
       }
     };
   } else if (searchMarker) {
     currentMarker = { 
       ...searchMarker, 
       isCurrentMarker: true,
       title: "Search Location",
       info: {
         address: searchMarker.address || input || 'Search Location',
         coordinates: `${searchMarker.lat}, ${searchMarker.lng}`,
         digipin: searchMarker.digipin || 'N/A',
         lat: searchMarker.lat,
         lng: searchMarker.lng
       }
     };
   } else if (userLocation) {
     console.log('Creating marker from userLocation:', userLocation);
     console.log('userLocation lat/lng check:', { hasLat: !!userLocation.lat, hasLng: !!userLocation.lng, lat: userLocation.lat, lng: userLocation.lng });
     currentMarker = { 
       ...userLocation, 
       isCurrentMarker: true,
       isDefault: true, // Mark as default user location
       title: "Your Location",
       info: {
         address: userLocation.address || 'Your Current Location',
         coordinates: `${userLocation.lat}, ${userLocation.lng}`,
         digipin: userLocation.digipin || 'N/A',
         lat: userLocation.lat,
         lng: userLocation.lng,
         isDefault: true // Mark as default user location in info too
       }
     };
     console.log('Created userLocation marker:', currentMarker);
   }
   
   // Always add the current marker first (it should be on top)
   if (currentMarker) {
     allMarkers.push(currentMarker);
     console.log('Added currentMarker to allMarkers:', currentMarker);
   } else {
     console.log('No currentMarker created - not adding to allMarkers');
   }
   
   // Add route markers with enhanced information ONLY if we have distances calculated
   // This ensures that when no saved location is selected, only the current marker is shown
   if (mapMarkers.length > 0 && distances.length > 0) {
     // Enhance route markers with destination information
     const enhancedRouteMarkers = mapMarkers.map(marker => ({
       ...marker,
       isRouteMarker: true,
       title: marker.label || "Nearest Location",
       info: {
         address: marker.label || marker.address || 'Nearest Location',
         coordinates: `${marker.lat}, ${marker.lng}`,
         digipin: marker.digipin || 'N/A',
         isDestination: true,
         lat: marker.lat,
         lng: marker.lng
       }
     }));
     allMarkers.push(...enhancedRouteMarkers);
   }
   
   // Add my location markers if checkbox is checked
   const myLocationMarkers = showMyLocations ? addresses.map(addr => ({ 
     lat: parseFloat(addr.lat), 
     lng: parseFloat(addr.lng), 
     label: addr.label || addr.address,
     isMyLocation: true,
     isDefault: defaultSavedLocation?.id === addr.id, // Mark default location
     title: addr.label || addr.address,
     info: {
       id: addr.id, // Include the ID for edit/delete operations
       address: addr.address,
       label: addr.label || 'My Location',
       coordinates: `${addr.lat}, ${addr.lng}`,
       digipin: addr.digipin || 'N/A',
       isMyLocation: true,
       isDefault: defaultSavedLocation?.id === addr.id, // Mark default location in info too
       input_type: addr.input_type // Include input type for editing
     }
     })) : [];
  if (myLocationMarkers.length > 0) allMarkers.push(...myLocationMarkers);

  // Add De-Dup markers if enabled
  if (enableDedup && dedupMarkers.length > 0) {
    console.log('Adding De-Dup markers to allMarkers:', dedupMarkers.length);
    console.log('Dedup markers data:', dedupMarkers);
    const dedupMapMarkers = dedupMarkers.map(marker => ({
      lat: marker.lat,
      lng: marker.lng,
      isDedupMarker: true,
      count: marker.count,
      digipin_prefix: marker.digipin_prefix,
      groupData: marker.groupData,
      bounds: marker.bounds,
      dedupLevel: dedupLevel, // Add dedupLevel directly to the marker object
      title: `De-Dup: ${marker.count} searches (Level ${dedupLevel})`,
      info: {
        address: `De-Dup Group: ${marker.digipin_prefix}`,
        coordinates: `${marker.lat}, ${marker.lng}`,
        digipin: marker.digipin_prefix,
        count: marker.count,
        level: dedupLevel,
        isDedupMarker: true
      }
    }));
    console.log('Dedup map markers created:', dedupMapMarkers);
    allMarkers.push(...dedupMapMarkers);
    console.log('Total markers after adding De-Dup:', allMarkers.length);
    console.log('All markers types:', allMarkers.map(m => ({ 
      isDedupMarker: m.isDedupMarker, 
      title: m.title,
      lat: m.lat,
      lng: m.lng
    })));
  } else {
    console.log('De-Dup markers not added - enableDedup:', enableDedup, 'dedupMarkers.length:', dedupMarkers.length);
  }

  // Calculate bounds for map zoom and determine zoom level
  let mapBounds = null;
  let mapZoom = 5; // Default zoom
  let mapCenter = defaultMapCenter; // Default center
  
  // If we're loading a search, preserve the current map center
  if (loading && searchMarker) {
    mapCenter = { lat: searchMarker.lat, lng: searchMarker.lng };
    mapZoom = 18;
  } else if (allMarkers.length > 0 && window.google) {
    // Filter out De-Dup markers for bounds calculation to prevent unwanted zoom out
    const nonDedupMarkers = allMarkers.filter(marker => !marker.isDedupMarker);
    const dedupMarkers = allMarkers.filter(marker => marker.isDedupMarker);
    
    if (nonDedupMarkers.length === 0 && dedupMarkers.length > 0) {
      // If only De-Dup markers exist, calculate bounds to fit all De-Dup areas
      const bounds = new window.google.maps.LatLngBounds();
      const padding = 0.01; // Smaller padding for De-Dup areas
      
      dedupMarkers.forEach(marker => {
        if (marker.bounds) {
          // Add the bounds of the De-Dup area
          bounds.extend(new window.google.maps.LatLng(marker.bounds.north, marker.bounds.east));
          bounds.extend(new window.google.maps.LatLng(marker.bounds.south, marker.bounds.west));
          
          // Add padding around the bounds
          bounds.extend(new window.google.maps.LatLng(
            marker.bounds.north + padding, 
            marker.bounds.east + padding
          ));
          bounds.extend(new window.google.maps.LatLng(
            marker.bounds.south - padding, 
            marker.bounds.west - padding
          ));
        } else {
          // Fallback to marker center if no bounds
          bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
        }
      });
      
      console.log('De-Dup bounds calculation:', {
        dedupMarkerCount: dedupMarkers.length,
        padding,
        bounds: {
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng()
        }
      });
      
      mapBounds = bounds;
    } else if (nonDedupMarkers.length === 1) {
      // For single non-De-Dup marker, center on the marker and use reasonable zoom
      mapZoom = 18;
      mapBounds = null;
      mapCenter = { lat: nonDedupMarkers[0].lat, lng: nonDedupMarkers[0].lng };
    } else if (nonDedupMarkers.length > 1) {
      // For multiple non-De-Dup markers, calculate bounds to fit all with padding for info windows
      mapZoom = 5; // Default zoom for multiple markers
      const bounds = new window.google.maps.LatLngBounds();
      const padding = 0.02; // Increased padding (approximately 2km) to ensure info windows are fully visible
      
      // Add only non-De-Dup markers to bounds with their actual info window positions
      nonDedupMarkers.forEach(marker => {
        // Add the marker position
        bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
        
        // Add the actual info window position (with offset)
        const infoWindowOffset = 0.0005; // Same offset as in InteractiveMap.js
        const infoWindowPosition = {
          lat: marker.lat + infoWindowOffset,
          lng: marker.lng
        };
        
        // Add padding around the info window position
        bounds.extend(new window.google.maps.LatLng(
          infoWindowPosition.lat + padding, 
          infoWindowPosition.lng + padding
        ));
        bounds.extend(new window.google.maps.LatLng(
          infoWindowPosition.lat - padding, 
          infoWindowPosition.lng - padding
        ));
      });
      
      // If route info window exists, include its position in bounds calculation
      const routeInfo = getRouteDistanceInfo();
      if (routeInfo && routeInfo.position) {
        bounds.extend(new window.google.maps.LatLng(
          routeInfo.position.lat + padding, 
          routeInfo.position.lng + padding
        ));
        bounds.extend(new window.google.maps.LatLng(
          routeInfo.position.lat - padding, 
          routeInfo.position.lng - padding
        ));
        
        console.log('Including route info window in bounds calculation:', {
          routeInfoPosition: routeInfo.position,
          padding
        });
      }
      
      console.log('Final bounds calculation including all info windows:', {
        markerCount: nonDedupMarkers.length,
        dedupMarkerCount: dedupMarkers.length,
        hasRouteInfo: !!routeInfo,
        padding,
        infoWindowOffset: 0.0005,
        markerPositions: nonDedupMarkers.map(m => ({ lat: m.lat, lng: m.lng })),
        infoWindowPositions: nonDedupMarkers.map(m => ({ 
          lat: m.lat + 0.0005, 
          lng: m.lng 
        })),
        bounds: {
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng()
        },
        boundsSize: {
          latSpan: bounds.getNorthEast().lat() - bounds.getSouthWest().lat(),
          lngSpan: bounds.getNorthEast().lng() - bounds.getSouthWest().lng()
        }
      });
      
      mapBounds = bounds;
    }
  }

  // Add this useEffect to fetch addresses when checkbox is checked
  useEffect(() => {
    if (showMyLocations && addresses.length === 0 && !addressesLoading) {
      refreshAddressesAndDefault();
    }
  }, [showMyLocations, addresses.length, addressesLoading, contextRefreshAddresses]);

  // Only recalculate bounds when user explicitly changes map-related settings
  useEffect(() => {
    // Force bounds recalculation only when checkbox state changes (user action)
    // Don't recalculate when addresses.length changes due to background refresh
    setBoundsKey(prev => prev + 1);
  }, [showMyLocations, distances.length]); // Removed addresses.length to prevent background refresh from resetting map

  // Fetch De-Dup data when settings change or search result changes
  useEffect(() => {
    console.log('useEffect triggered - enableDedup:', enableDedup, 'dedupLevel:', dedupLevel, 'addressSimilarityThreshold:', addressSimilarityThreshold);
    fetchDedupData();
  }, [enableDedup, dedupLevel, addressSimilarityThreshold, result, searchMarker, mapMarker]);

  // Filter recent searches based on input
  const filteredRecentSearches = inputHistory.filter(item => 
    item.value.toLowerCase().includes(recentSearchTerm.toLowerCase())
  ).slice(0, 10);

  // Filter specific location addresses
  const filteredSpecificAddresses = addresses.filter(addr => {
    const searchTerm = specificLocationSearch.toLowerCase();
    const addrLabel = (addr.label || '').toLowerCase();
    const addrAddress = (addr.address || '').toLowerCase();
    
    return addrLabel.includes(searchTerm) || addrAddress.includes(searchTerm);
  }).slice(0, 10);

  // Handle Specific Location button click
  const handleSpecificLocationClick = () => {
    // Check if there's already a populated location in the search field
    if (specificLocationSearch && specificLocationSearch.trim() !== '') {
      // Find the matching address from the addresses list (case-insensitive)
      const searchTerm = specificLocationSearch.toLowerCase().trim();
      const matchingAddress = addresses.find(addr => {
        const addrLabel = (addr.label || '').toLowerCase();
        const addrAddress = (addr.address || '').toLowerCase();
        
        return addrLabel === searchTerm || 
               addrAddress === searchTerm ||
               addrLabel.includes(searchTerm) ||
               addrAddress.includes(searchTerm);
      });
      
      if (matchingAddress) {
        // If we found a matching address, calculate route to it
        console.log('Using pre-populated location:', matchingAddress);
        
        // Determine which location data to use (priority: result > mapMarker > searchMarker > userLocation)
        let currentLocation = null;
        
        if (result && result.lat && result.lng) {
          currentLocation = result;
        } else if (mapMarker && mapMarker.lat && mapMarker.lng) {
          currentLocation = mapMarker;
        } else if (searchMarker && searchMarker.lat && searchMarker.lng) {
          currentLocation = searchMarker;
        } else if (userLocation && userLocation.lat && userLocation.lng) {
          currentLocation = userLocation;
        }
        
        // Calculate distance between current location and selected specific location
        if (currentLocation) {
          console.log('Calling handleSpecificLocationDistanceCalculation with current location:', currentLocation);
          setRouteCalculationType('specific');
          handleSpecificLocationDistanceCalculation(matchingAddress, currentLocation);
        } else {
          console.log('No current location available for distance calculation');
          setRouteError('Please select a location first by clicking on the map or searching.');
        }
        return;
      }
    }
    
    // If no populated location or no match found, show the dropdown for selection
    setShowSpecificLocationField(true);
    setSpecificLocationDropdownOpen(true);
    setHighlightedSpecificIndex(-1);
  };

  // Handle specific location selection
  const selectSpecificLocation = (addr) => {
    console.log('selectSpecificLocation called with:', addr);
    setSpecificLocationSearch(addr.address);
    setSpecificLocationDropdownOpen(false);
    setHighlightedSpecificIndex(-1);
  };

  // Handle specific location distance calculation - using same pattern as nearest location
  const handleSpecificLocationDistanceCalculation = async (selectedAddress, currentLocation) => {
    console.log('handleSpecificLocationDistanceCalculation called with:', { selectedAddress, currentLocation });
    
    if (!currentLocation || !currentLocation.lat || !currentLocation.lng) {
      setRouteError('Please get a location first by searching, clicking on the map, or allowing location access.');
      return;
    }
    
    console.log('Using location data for specific location calculation:', {
      source: result ? 'map_click' : mapMarker ? 'map_click' : searchMarker ? 'search' : 'user_location',
      currentLocation: {
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        address: currentLocation.address
      },
      selectedAddress: selectedAddress
    });

    setRouteLoading(true);
    setRouteError('');
    setDistances([]);
    setNewAddressCoords(null);
    clearRoute();

    try {
      // Use the efficient distance-between API to get road distance for the selected location
      const location1 = `${currentLocation.lat},${currentLocation.lng}`; // Current location coordinates
      const location2 = selectedAddress.address; // Selected location address
      
      const response = await authenticatedFetch('/distance-between', {
        method: 'POST',
        body: JSON.stringify({ 
          location1: location1, 
          location2: location2 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Calculate aerial distance using mathematical formula
        const aerialDistance = calculateHaversineDistance(
          currentLocation.lat, currentLocation.lng,
          parseFloat(selectedAddress.lat), parseFloat(selectedAddress.lng)
        );
        
        // Create the final result with both aerial and road distances (same format as nearest location)
        const finalResult = {
          address: selectedAddress.address,
          label: selectedAddress.label,
          aerial_distance_km: aerialDistance,
          road_distance_km: data.road_distance_km,
          road_duration: data.duration,
          lat: selectedAddress.lat,
          lng: selectedAddress.lng,
          digipin: selectedAddress.digipin
        };
        
        // Set the result in distances array (same as nearest location)
        setDistances([finalResult]);
        
        // Set the current location coordinates (same as nearest location)
        setNewAddressCoords({
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          address: currentLocation.address,
          digipin: currentLocation.digipin
        });

        // Calculate route to the selected address (same as nearest location)
        const origin = `${currentLocation.lat},${currentLocation.lng}`;
        const destination = `${selectedAddress.lat},${selectedAddress.lng}`;
        
        const routeAddresses = {
          origin: {
            address: currentLocation.address,
            lat: currentLocation.lat,
            lng: currentLocation.lng,
            digipin: currentLocation.digipin
          },
          destination: {
            address: selectedAddress.address,
            lat: selectedAddress.lat,
            lng: selectedAddress.lng,
            digipin: selectedAddress.digipin
          }
        };
        
        // Add destination marker to mapMarkers so it's always visible (same as nearest location)
        setMapMarkers([{ 
          lat: parseFloat(selectedAddress.lat), 
          lng: parseFloat(selectedAddress.lng),
          label: selectedAddress.label || selectedAddress.address,
          isRouteMarker: true,
          digipin: selectedAddress.digipin
        }]);
        
        await calculateRoute(
          `${routeAddresses.origin.lat},${routeAddresses.origin.lng}`,
          `${routeAddresses.destination.lat},${routeAddresses.destination.lng}`,
          routeAddresses
        );
      } else {
        // If API call fails, show only the selected location with aerial distance (same as nearest location)
        const aerialDistance = calculateHaversineDistance(
          currentLocation.lat, currentLocation.lng,
          parseFloat(selectedAddress.lat), parseFloat(selectedAddress.lng)
        );
        
        const aerialOnlyResult = {
          address: selectedAddress.address,
          label: selectedAddress.label,
          aerial_distance_km: aerialDistance,
          road_distance_km: null,
          road_duration: null,
          lat: selectedAddress.lat,
          lng: selectedAddress.lng,
          digipin: selectedAddress.digipin
        };
        
        setDistances([aerialOnlyResult]);
        
        // Set the selected location coordinates
        setNewAddressCoords({
          lat: parseFloat(selectedAddress.lat),
          lng: parseFloat(selectedAddress.lng),
          digipin: selectedAddress.digipin
        });

        // Create route addresses object for map display
        const routeAddresses = {
          origin: {
            address: currentLocation.address,
            lat: currentLocation.lat,
            lng: currentLocation.lng,
            digipin: currentLocation.digipin
          },
          destination: {
            address: selectedAddress.address,
            lat: selectedAddress.lat,
            lng: selectedAddress.lng,
            digipin: selectedAddress.digipin
          }
        };

        // Add destination marker to mapMarkers so it's always visible
        setMapMarkers([{ 
          lat: parseFloat(selectedAddress.lat), 
          lng: parseFloat(selectedAddress.lng),
          label: selectedAddress.label || selectedAddress.address,
          isRouteMarker: true,
          digipin: selectedAddress.digipin
        }]);

        await calculateRoute(
          `${routeAddresses.origin.lat},${routeAddresses.origin.lng}`,
          `${routeAddresses.destination.lat},${routeAddresses.destination.lng}`,
          routeAddresses
        );
      }
    } catch (error) {
      setRouteError('Error calculating distances. Please try again.');
      throw error;
    } finally {
      setRouteLoading(false);
    }
  };

  // Handle specific location input key events
  const handleSpecificLocationKeyDown = (e) => {
    if (specificLocationDropdownOpen && filteredSpecificAddresses.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedSpecificIndex((prev) => (prev + 1) % filteredSpecificAddresses.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedSpecificIndex((prev) => (prev - 1 + filteredSpecificAddresses.length) % filteredSpecificAddresses.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedSpecificIndex >= 0 && highlightedSpecificIndex < filteredSpecificAddresses.length) {
          selectSpecificLocation(filteredSpecificAddresses[highlightedSpecificIndex]);
        }
      } else if (e.key === 'Escape') {
        setSpecificLocationDropdownOpen(false);
        setHighlightedSpecificIndex(-1);
      }
    }
  };

  // Handle click outside to close specific location dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        specificDropdownRef.current &&
        !specificDropdownRef.current.contains(event.target) &&
        specificLocationRef.current &&
        !specificLocationRef.current.contains(event.target)
      ) {
        setSpecificLocationDropdownOpen(false);
        setHighlightedSpecificIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle recent search selection
  const selectRecentSearch = (historyInput) => {
    // Clear existing routes and markers when selecting a new address from search
    clearRoute();
    setMapMarkers([]);
    setDistances([]);
    setNewAddressCoords(null);
    setRouteError('');
    
    // Clear specific location dropdown but keep the field visible
    setSpecificLocationDropdownOpen(false);
    
    setInput(historyInput.value);
    setRecentSearchTerm("");
    setShowRecentSearchesDropdown(false);
    setHighlightedRecentIndex(-1);
    // Trigger search with the selected item
    handleInputClick(historyInput);
  };

  // Handle main search input key events
  const handleMainSearchKeyDown = (e) => {
    if (showRecentSearchesDropdown && filteredRecentSearches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedRecentIndex((prev) => (prev + 1) % filteredRecentSearches.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedRecentIndex((prev) => (prev - 1 + filteredRecentSearches.length) % filteredRecentSearches.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedRecentIndex >= 0 && highlightedRecentIndex < filteredRecentSearches.length) {
          selectRecentSearch(filteredRecentSearches[highlightedRecentIndex]);
        } else {
          // Normal search functionality
          handleSearch(e);
        }
      } else if (e.key === 'Escape') {
        setShowRecentSearchesDropdown(false);
        setHighlightedRecentIndex(-1);
      }
    } else if (e.key === 'Enter') {
      // Normal search functionality
      handleSearch(e);
    }
  };

  // Handle click outside to close recent searches dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        recentDropdownRef.current &&
        !recentDropdownRef.current.contains(event.target) &&
        mainSearchRef.current &&
        !mainSearchRef.current.contains(event.target)
      ) {
        setShowRecentSearchesDropdown(false);
        setHighlightedRecentIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Location management handler functions
  const resetForm = () => {
    setFormData({
      input: '',
      label: '',
      inputType: 'address',
      place_id: null,
      setAsDefault: false
    });
    setEditingLocation(null);
    setShowAddModal(false);
    setShowEditModal(false);
    setFormMessage('');
  };

  const handleAddToMyLocation = (locationInfo) => {
    console.log('handleAddToMyLocation called with:', locationInfo);
    
    // Smart default behavior:
    // - If user has no saved locations, set as default (first location)
    // - If user already has saved locations, don't set as default (let user choose)
    const shouldSetAsDefault = addresses.length === 0;
    
    setFormData({
      input: locationInfo.address,
      label: locationInfo.label || locationInfo.address,
      inputType: 'address',
      place_id: locationInfo.place_id || null,
      setAsDefault: shouldSetAsDefault
    });
    setShowAddModal(true);
  };

  const handleEditLocation = (locationInfo) => {
    console.log('Edit location info:', locationInfo);
    console.log('Available addresses:', addresses);
    
    // Find the actual address object from the addresses array
    let actualAddress = addresses.find(addr => 
      addr.address === locationInfo.address && 
      addr.label === locationInfo.label
    );
    
    // Fallback: try to find by address only if exact match fails
    if (!actualAddress) {
      actualAddress = addresses.find(addr => addr.address === locationInfo.address);
    }
    
    // Fallback: try to find by label only if address match fails
    if (!actualAddress) {
      actualAddress = addresses.find(addr => addr.label === locationInfo.label);
    }
    
    console.log('Found actual address:', actualAddress);
    
    if (!actualAddress) {
      setFormMessage('Error: Location not found in your saved locations.');
      return;
    }
    
    setEditingLocation(actualAddress);
    setFormData({
      input: actualAddress.address,
      label: actualAddress.label || actualAddress.address,
      inputType: actualAddress.input_type || 'address',
      place_id: actualAddress.place_id || null,
      setAsDefault: defaultSavedLocation?.id === actualAddress.id
    });
    setShowEditModal(true);
  };

  const handleDeleteLocation = async (locationInfo) => {
    console.log('Delete location info:', locationInfo);
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await contextRefreshAddresses();
        
        // Find the actual address object from the addresses array
        const addressToDelete = addresses.find(addr => 
          addr.address === locationInfo.address && 
          addr.label === locationInfo.label
        );
        
        if (addressToDelete) {
          await authenticatedFetch(`/addresses/${addressToDelete.id}`, { method: 'DELETE' });
          await refreshAddressesAndDefault();
          setFormMessage('Location deleted successfully!');
        } else {
          setFormMessage('Error: Location not found in your saved locations.');
        }
      } catch (error) {
        setFormMessage(`Error: ${error.message || 'Could not delete location.'}`);
      }
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    if (!formData.input.trim() || !formData.label.trim()) {
      setFormMessage('Please provide both input and label.');
      return;
    }

    setFormLoading(true);
    setFormMessage('');

    try {
      const response = await authenticatedFetch('/addresses', {
        method: 'POST',
        body: JSON.stringify({
          address: formData.input,
          label: formData.label,
          input_type: formData.inputType,
          place_id: formData.place_id
        }),
      });

      if (response.ok) {
        const addedLocation = await response.json();
        
        // If user wants to set this as default location
        if (formData.setAsDefault) {
          const defaultResponse = await authenticatedFetch('/addresses/set-default', {
            method: 'POST',
            body: JSON.stringify({
              address_id: addedLocation.id
            }),
          });
          
          if (defaultResponse.ok) {
            setFormMessage('Location added and set as default location successfully!');
          } else {
            setFormMessage('Location added successfully, but failed to set as default location.');
          }
        } else {
          setFormMessage('Location added successfully!');
        }
        
        resetForm();
        await refreshAddressesAndDefault();
      } else {
        const errorData = await response.json();
        setFormMessage(`Error: ${errorData.detail || errorData.message || 'Failed to add location.'}`);
      }
    } catch (error) {
      setFormMessage(`Error: ${error.message || 'Could not save location. Please try again.'}`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditAddress = async (e) => {
    e.preventDefault();
    if (!formData.input.trim() || !formData.label.trim()) {
      setFormMessage('Please provide both input and label.');
      return;
    }

    setFormLoading(true);
    setFormMessage('');

    try {
      if (!editingLocation || !editingLocation.id) {
        setFormMessage('Error: Invalid location data. Please try again.');
        return;
      }
      
      const response = await authenticatedFetch(`/addresses/${editingLocation.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          address: formData.input,
          label: formData.label,
          input_type: formData.inputType,
          place_id: formData.place_id
        }),
      });

      if (response.ok) {
        // If user wants to set this as default location
        if (formData.setAsDefault) {
          const defaultResponse = await authenticatedFetch('/addresses/set-default', {
            method: 'POST',
            body: JSON.stringify({
              address_id: editingLocation.id
            }),
          });
          
          if (defaultResponse.ok) {
            setFormMessage('Location updated and set as default location successfully!');
          } else {
            setFormMessage('Location updated successfully, but failed to set as default location.');
          }
        } else {
          setFormMessage('Location updated successfully!');
        }
        
        resetForm();
        await refreshAddressesAndDefault();
      } else {
        const errorData = await response.json();
        setFormMessage(`Error: ${errorData.detail || errorData.message || 'Failed to update location.'}`);
      }
    } catch (error) {
      setFormMessage(`Error: ${error.message || 'Could not update location. Please try again.'}`);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Full-screen map container */}
      <div style={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1
      }}>
        {!locationLoading ? (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <InteractiveMap
              key={mapKey}
              markerPositions={(() => {
                const markers = allMarkers.length > 0 ? allMarkers : (userLocation ? [userLocation] : []);
                console.log('Passing markers to InteractiveMap:', markers);
                console.log('allMarkers.length:', allMarkers.length);
                console.log('userLocation for fallback:', userLocation);
                return markers;
              })()}
              clickable={true}
              onMapClick={handleMapClick}
              defaultCenter={mapCenter}
              zoom={mapZoom}
              mapType={mapType}
              satelliteLabels={satelliteLabels}
              directions={directions}
              bounds={mapBounds}
              routeDistanceInfo={getRouteDistanceInfo()}
              onNearestLocationClick={handleNearestLocation}
              onSpecificLocationClick={handleSpecificLocationClick}
              showInfoWindows={false}
              userEmail={userEmail}
              onAddToMyLocation={handleAddToMyLocation}
              onEditLocation={handleEditLocation}
              onDeleteLocation={handleDeleteLocation}
              onDedupMarkerClick={handleDedupMarkerClick}
              dedupRectangles={dedupRectangles}
              dedupRectanglesKey={dedupRectanglesKey}
            />
            
            {(mapClickLoading || addressesLoading || routeLoading || loading) && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #007bff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 10px'
                  }}></div>
                  <p style={{ margin: 0, color: '#333', fontSize: '16px', fontWeight: '500' }}>
                    {addressesLoading ? 'Loading your locations...' : 
                     routeLoading ? (routeCalculationType === 'specific' ? 'Calculating distance from selected location...' : 'Calculating nearest location...') : 
                     loading ? 'Searching for location...' :
                     'Getting location details...'}
                  </p>
                  <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                    {addressesLoading ? 'Please wait while we fetch your saved locations' : 
                     routeLoading ? (routeCalculationType === 'specific' ? 'Please wait while we calculate the route to your selected location' : 'Please wait while we calculate the route to your nearest location') : 
                     loading ? 'Please wait while we search for your location and get details' :
                     'Please wait while we fetch the information'}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: '1 1 0%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', color: '#666', fontSize: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <p>{locationLoading ? 'Detecting your location...' : 'Loading map...'}</p>
              <p style={{ fontSize: '14px', marginTop: '10px' }}>
                {locationLoading ? 'Please wait while we detect your location.' : 'Please wait while we load the map.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Floating navigation buttons - top right horizontal */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '100px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'row',
        gap: '8px'
      }}>
        
        
        <Link to="/pin2point-leaflet" style={{
          background: 'white',
          border: '1px solid #28a745',
          borderRadius: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '13px',
          fontWeight: '500',
          textDecoration: 'none',
          color: '#28a745',
          width: 'auto',
          boxSizing: 'border-box'
        }}>
          <span style={{ fontSize: '16px' }}>🗺️</span>
          Leaflet Map
        </Link>
        
        <Link to="/bulk-search" style={{
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '13px',
          fontWeight: '500',
          textDecoration: 'none',
          color: '#333',
          width: 'auto',
          boxSizing: 'border-box'
        }}>
          <span style={{ fontSize: '16px' }}>📊</span>
          Batch Processor
        </Link>
        
        <Link to="/usage-history" style={{
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '13px',
          fontWeight: '500',
          textDecoration: 'none',
          color: '#333',
          width: 'auto',
          boxSizing: 'border-box'
        }}>
          <span style={{ fontSize: '16px' }}>📋</span>
          History
        </Link>
        
        {user?.is_admin && (
          <>
            <div style={{
              background: '#f8f9fa',
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '11px',
              color: '#666',
              fontWeight: '500',
              textAlign: 'center',
              width: 'auto',
              boxSizing: 'border-box'
            }}>
              Admin
            </div>
            
            <Link to="/admin" style={{
              background: 'white',
              border: '1px solid #dc3545',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: '13px',
              fontWeight: '500',
              textDecoration: 'none',
              color: '#dc3545',
              width: 'auto',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: '16px' }}>⚙️</span>
              Admin Dashboard
            </Link>
            
            <Link to="/admin/users" style={{
              background: 'white',
              border: '1px solid #dc3545',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: '13px',
              fontWeight: '500',
              textDecoration: 'none',
              color: '#dc3545',
              width: 'auto',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: '16px' }}>👥</span>
              Users
            </Link>
            
            <Link to="/admin/usage" style={{
              background: 'white',
              border: '1px solid #dc3545',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: '13px',
              fontWeight: '500',
              textDecoration: 'none',
              color: '#dc3545',
              width: 'auto',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: '16px' }}>📋</span>
              Logs
            </Link>
          </>
        )}
      </div>

      {/* Settings button - positioned to avoid map fullscreen button */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '50px',
        zIndex: 1000
      }} data-dropdown>
        <button
          style={{
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '50%',
            width: '42px',
            height: '42px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            fontSize: '18px'
          }}
          onClick={() => setShowDropdown(prev => !prev)}
        >
          ⚙️
        </button>
        {showDropdown && (
          <div style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: '8px',
            background: 'white',
            minWidth: '140px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: '6px',
            zIndex: 2000,
            border: '1px solid #eee'
          }}>
            <Link to="/profile" style={{ display: 'block', padding: '10px 12px', color: '#333', textDecoration: 'none', borderBottom: '1px solid #eee', fontSize: '13px' }} onClick={() => setShowDropdown(false)}>Profile</Link>
            <button 
              onClick={() => {
                handleLogout();
                setShowDropdown(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 12px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                color: '#dc3545',
                fontSize: '13px'
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Search panel - left side aligned with horizontal buttons */}
      <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 1000,
        width: '300px',
        maxHeight: 'calc(100vh - 20px)',
        overflowY: 'auto',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        border: '1px solid #eee'
      }}>
        {/* Input Section */}
        <div style={{
          padding: '15px',
          borderBottom: '1px solid #eee'
        }}>
          <form onSubmit={handleSearch}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>
                Search Location:
              </label>
              <div style={{ position: 'relative' }} ref={recentDropdownRef}>
                <input
                  ref={mainSearchRef}
                  type="text"
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setRecentSearchTerm(e.target.value);
                    setShowRecentSearchesDropdown(true);
                    setHighlightedRecentIndex(-1);
                  }}
                  onFocus={() => {
                    setShowRecentSearchesDropdown(true);
                    setHighlightedRecentIndex(-1);
                  }}
                  onKeyDown={handleMainSearchKeyDown}
                  placeholder="Enter address or lat,lng to search"
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                
                {/* Recent Searches Dropdown */}
                {showRecentSearchesDropdown && filteredRecentSearches.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    marginTop: '2px'
                  }}>
                    {filteredRecentSearches.map((historyInput, index) => {
                      // Find the most recent record for this input
                      const record = usageHistory
                        .filter(r => (historyInput.type === 'address' && r.address === historyInput.value) ||
                                     (historyInput.type === 'digipin' && r.digipin === historyInput.value))
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
                      
                      const safeAddress = typeof record?.address === 'string' ? record.address : historyInput.value;
                      
                      return (
                        <div
                          key={index}
                          onClick={() => selectRecentSearch(historyInput)}
                          style={{
                            padding: '8px',
                            cursor: 'pointer',
                            backgroundColor: index === highlightedRecentIndex ? '#f0f0f0' : 'transparent',
                            borderBottom: index < filteredRecentSearches.length - 1 ? '1px solid #eee' : 'none'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{historyInput.value}</div>
                          <div style={{ fontSize: '11px', color: '#666' }}>{safeAddress}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Searching...' : 'Search Location'}
              </button>
              <button
                type="button"
                onClick={clearAll}
                style={{
                  padding: '10px 12px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
                title="Clear all"
              >
                Clear
              </button>
            </div>
          </form>
          
          {/* Always Visible Specific Location Field */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>
              My Location:
            </label>
            <div style={{ position: 'relative' }} ref={specificDropdownRef}>
              <div style={{ position: 'relative' }}>
                <input
                  ref={specificLocationRef}
                  type="text"
                  value={specificLocationSearch}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setSpecificLocationSearch(newValue);
                    
                    // Show dropdown when field is cleared or has content
                    if (newValue.trim() === '') {
                      // Field is cleared - show all saved locations
                      setSpecificLocationDropdownOpen(true);
                      setHighlightedSpecificIndex(-1);
                    } else {
                      // Field has content - show filtered locations
                      setSpecificLocationDropdownOpen(true);
                      setHighlightedSpecificIndex(-1);
                    }
                  }}
                  onFocus={() => {
                    setSpecificLocationDropdownOpen(true);
                    setHighlightedSpecificIndex(-1);
                  }}
                  onKeyDown={handleSpecificLocationKeyDown}
                  placeholder="Search my locations..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    paddingRight: specificLocationSearch ? '35px' : '10px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                {specificLocationSearch && (
                  <button
                    type="button"
                    onClick={() => {
                      setSpecificLocationSearch('');
                      setSpecificLocationDropdownOpen(true);
                      setHighlightedSpecificIndex(-1);
                    }}
                    style={{
                      position: 'absolute',
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      fontSize: '16px',
                      cursor: 'pointer',
                      color: '#999',
                      padding: '2px',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Clear field"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Specific Location Dropdown */}
              {specificLocationDropdownOpen && (
                <div style={{
                  position: 'fixed',
                  top: specificDropdownRef.current ? specificDropdownRef.current.getBoundingClientRect().bottom + 2 : 'auto',
                  left: specificDropdownRef.current ? specificDropdownRef.current.getBoundingClientRect().left : 'auto',
                  width: specificDropdownRef.current ? specificDropdownRef.current.offsetWidth : 'auto',
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  {filteredSpecificAddresses.length > 0 ? (
                    filteredSpecificAddresses.map((addr, index) => (
                      <div
                        key={addr.id}
                        onClick={() => selectSpecificLocation(addr)}
                        style={{
                          padding: '8px',
                          cursor: 'pointer',
                          backgroundColor: index === highlightedSpecificIndex ? '#f0f0f0' : 'transparent',
                          borderBottom: index < filteredSpecificAddresses.length - 1 ? '1px solid #eee' : 'none'
                        }}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                          {addr.label || addr.address}
                          {defaultSavedLocation && defaultSavedLocation.id === addr.id && (
                            <span style={{ color: '#28a745', fontSize: '11px', marginLeft: '6px' }}>
                              ✓ Default
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>{addr.address}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '8px', color: '#666', fontSize: '13px', textAlign: 'center' }}>
                      No saved locations found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
                      <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showMyLocations}
                  onChange={(e) => setShowMyLocations(e.target.checked)}
                  style={{ margin: 0 }}
                />
                <span style={{ fontSize: '13px' }}>Show My Locations on Map</span>
              </label>
            </div>

            {/* De-Dup Control */}
            <DedupControl
              enableDedup={enableDedup}
              setEnableDedup={setEnableDedup}
              dedupLevel={dedupLevel}
              setDedupLevel={setDedupLevel}
              dedupData={dedupData}
              loading={dedupLoading}
              setDedupData={setDedupData}
              setDedupMarkers={setDedupMarkers}
              setSelectedDedupGroup={setSelectedDedupGroup}
              setShowDedupModal={setShowDedupModal}
              setDedupRectangles={setDedupRectangles}
              dedupRectangles={dedupRectangles}
              setDedupRectanglesKey={setDedupRectanglesKey}
              dedupRectanglesKey={dedupRectanglesKey}
              addressSimilarityThreshold={addressSimilarityThreshold}
              setAddressSimilarityThreshold={setAddressSimilarityThreshold}
            />

          {error && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24',
              fontSize: '12px'
            }}>
              {error}
            </div>
          )}
        </div>
      </div>
      
      {/* Location Source Message */}
      <LocationSourceMessage 
        locationSource={locationSource}
        onClose={() => setShowLocationMessage(false)}
      />

      {/* Location Permission Prompt Modal */}
      {showLocationMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#3B82F6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px'
            }}>
              📍
            </div>
            
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '18px', fontWeight: '600' }}>
              Location Access Required
            </h3>
            
            <p style={{ 
              margin: '0 0 24px 0', 
              color: '#6b7280', 
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              To view the Digipin of your location, please allow location sharing or set a default location.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  console.log('🔵 Allow Location button clicked');
                  handleLocationPermissionGranted();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563EB';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3B82F6';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span className="material-icons" style={{ fontSize: '18px' }}>
                  my_location
                </span>
                Allow Location
              </button>
              
              <button
                onClick={() => {
                  console.log('🟢 Set Default Location button clicked');
                  handleSetDefaultLocation();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#059669';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#10B981';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span className="material-icons" style={{ fontSize: '18px' }}>
                  home
                </span>
                Set Default Location
              </button>
              
              <button
                onClick={() => {
                  console.log('⚫ Don\'t Allow button clicked');
                  handleLocationPermissionDenied();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#4B5563';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6B7280';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Don't Allow
              </button>
            </div>
            
            <p style={{ 
              margin: '16px 0 0 0', 
              color: '#9ca3af', 
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              Sharing your location will allow us to show you the Digipin of your exact location. 
              If you prefer not to share your exact location, we will show approximate location which might not be accurate. 
              You can always click anywhere on the map to find the Digipin of that location.
            </p>
          </div>
        </div>
      )}

      {/* Add Location Modal */}
      <Modal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add to My Location"
      >
        <form onSubmit={handleAddAddress}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Input Type:
              </label>
              <select
                value={formData.inputType}
                onChange={(e) => setFormData(prev => ({ ...prev, inputType: e.target.value }))}
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="address">Address</option>
                <option value="latlng">Latitude, Longitude</option>
                <option value="digipin">Digipin</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {formData.inputType === 'address' ? 'Address:' :
                 formData.inputType === 'latlng' ? 'Latitude, Longitude:' : 'Digipin:'}
              </label>
              <input
                type="text"
                value={formData.input}
                onChange={(e) => setFormData(prev => ({ ...prev, input: e.target.value }))}
                placeholder={
                  formData.inputType === 'address' ? 'Enter address...' :
                  formData.inputType === 'latlng' ? 'e.g., 28.6139,77.2090' : 'e.g., 4T3-7F2-2LJT'
                }
                required
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Label:
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Enter a label..."
                required
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.setAsDefault}
                  onChange={(e) => setFormData(prev => ({ ...prev, setAsDefault: e.target.checked }))}
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontWeight: '500', fontSize: '14px' }}>
                  Set as default location
                </span>
              </label>
            </div>
          </div>
          
          {formMessage && (
            <div style={{ 
              marginTop: '16px', 
              padding: '8px', 
              backgroundColor: formMessage.includes('Error') ? '#f8d7da' : '#d4edda',
              border: `1px solid ${formMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
              borderRadius: '4px',
              color: formMessage.includes('Error') ? '#721c24' : '#155724',
              fontSize: '14px'
            }}>
              {formMessage}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button 
              type="button"
              onClick={() => setShowAddModal(false)}
              style={{ 
                padding: '10px 20px', 
                background: '#6c757d', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={formLoading || !formData.input.trim() || !formData.label.trim()}
              style={{ 
                padding: '10px 20px', 
                background: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: formLoading ? 'not-allowed' : 'pointer',
                opacity: formLoading ? 0.6 : 1
              }}
            >
              {formLoading ? 'Saving...' : 'Add Location'}
            </button>
          </div>
        </form>
      </Modal>
      
      {/* Edit Location Modal */}
      <Modal 
        isOpen={showEditModal} 
        onClose={() => setShowEditModal(false)}
        title="Edit Location"
      >
        <form onSubmit={handleEditAddress}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Input Type:
              </label>
              <select
                value={formData.inputType}
                onChange={(e) => setFormData(prev => ({ ...prev, inputType: e.target.value }))}
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="address">Address</option>
                <option value="latlng">Latitude, Longitude</option>
                <option value="digipin">Digipin</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {formData.inputType === 'address' ? 'Address:' :
                 formData.inputType === 'latlng' ? 'Latitude, Longitude:' : 'Digipin:'}
              </label>
              <input
                type="text"
                value={formData.input}
                onChange={(e) => setFormData(prev => ({ ...prev, input: e.target.value }))}
                placeholder={
                  formData.inputType === 'address' ? 'Enter address...' :
                  formData.inputType === 'latlng' ? 'e.g., 28.6139,77.2090' : 'e.g., 4T3-7F2-2LJT'
                }
                required
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Label:
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Enter a label..."
                required
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #dee2e6'
            }}>
              <input
                type="checkbox"
                id="setAsDefault"
                checked={formData.setAsDefault}
                onChange={(e) => setFormData(prev => ({ ...prev, setAsDefault: e.target.checked }))}
                style={{ 
                  width: '16px', 
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              <label 
                htmlFor="setAsDefault"
                style={{ 
                  margin: 0, 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#495057'
                }}
              >
                Set as default location
              </label>
              {formData.setAsDefault && (
                <span style={{ 
                  fontSize: '12px', 
                  color: '#28a745', 
                  marginLeft: '8px',
                  fontWeight: '500'
                }}>
                  ✓ This location will be used as your default
                </span>
              )}
            </div>
          </div>
          
          {formMessage && (
            <div style={{ 
              marginTop: '16px', 
              padding: '8px', 
              backgroundColor: formMessage.includes('Error') ? '#f8d7da' : '#d4edda',
              border: `1px solid ${formMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
              borderRadius: '4px',
              color: formMessage.includes('Error') ? '#721c24' : '#155724',
              fontSize: '14px'
            }}>
              {formMessage}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button 
              type="button"
              onClick={() => setShowEditModal(false)}
              style={{ 
                padding: '10px 20px', 
                background: '#6c757d', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={formLoading || !formData.input.trim() || !formData.label.trim()}
              style={{ 
                padding: '10px 20px', 
                background: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: formLoading ? 'not-allowed' : 'pointer',
                opacity: formLoading ? 0.6 : 1
              }}
            >
              {formLoading ? 'Saving...' : 'Update Location'}
            </button>
          </div>
        </form>
      </Modal>

      {/* De-Dup Transaction Modal */}
      <DedupTransactionModal
        isOpen={showDedupModal}
        onClose={() => setShowDedupModal(false)}
        groupData={selectedDedupGroup}
        dedupLevel={dedupLevel}
      />
    </div>
  );
};

export default Pin2PointMain;
